/**
 * Tool and endpoint-execution types shared across the codebase:
 *   - `ToolRendererConfig`     — used by compound-main, ai-view-display,
 *                                map-display-search, ai-chat-toolbar, map-container,
 *                                phoenix-viewer-container, phoenix-viewer-page
 *   - `ExecutionResult`        — used by endpoint-execution and response-interpreter services
 *   - `ExecutionError`         — used by endpoint-execution and response-interpreter services
 *   - `ExecutionResultType`    — used by endpoint-execution and response-interpreter services
 *   - `RawExecutionResponse`   — used by endpoint-execution and response-interpreter services
 *   - `MissingParam`/`ApiEndpointValue` — API endpoint block emitted by the LangGraph agent
 *                                on the execute path (matches enrichment_block_spec.md)
 */

// ---------------------------------------------------------------------------
// API Endpoint block — matches enrichment_block_spec.md
// ---------------------------------------------------------------------------

/** A parameter that could not be resolved by the agent. */
export interface MissingParam {
  /** Parameter name as it appears in the API spec. */
  name: string;
  /**
   * Data type hint for the input widget.
   * Common values: "string", "number", "integer", "boolean",
   *                "wkt", "bbox", "date", "array"
   */
  type: string;
  /** Short human-readable description of what value is expected. */
  description: string;
}

/**
 * API endpoint block emitted by the LangGraph agent on the execute path.
 * Matches enrichment_block_spec.md exactly.
 *
 * Key rules:
 * - Use `id` as Angular track-by key, NOT `operation_id` (can repeat in a session)
 * - Use `status` as the authoritative signal, NOT `missing_required.length`
 * - Incomplete URLs contain `<description>` placeholders for unresolved params
 * - `resolved_params` and `missing_required` are absent on the explore path
 *   (explore info arrives as markdown text in the message, not as a block)
 */
export interface ApiEndpointValue {
  /** Unique block identifier. Always use this for Angular keys/tracking. */
  id: string;
  /** API operation identifier from the catalog (e.g. "ortho_extract"). */
  operation_id: string;
  /** Primary HTTP verb used for execution. */
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** All HTTP verbs supported by this endpoint (for secondary method badges). */
  methods: Array<"GET" | "POST" | "PUT" | "PATCH" | "DELETE">;
  /**
   * Full URL. When status="complete" all params are substituted.
   * When status="incomplete" unresolved params appear as `<description>` placeholders,
   * e.g. `?tile-x=<The x coordinate of the tile. (Datum: ITRF2008).>`
   */
  url: string;
  /** Human-readable endpoint description from the API catalog. */
  description: string;
  /**
   * "complete"   — all required params resolved; URL is ready to execute.
   * "incomplete" — one or more required params missing; show the param form.
   */
  status: "complete" | "incomplete";
  /** Resolved parameter values (present on execute path). */
  resolved_params?: Record<string, string>;
  /** Missing required parameters with type hints (present on execute path, status="incomplete"). */
  missing_required?: MissingParam[];
  /** Optional cURL example for the examples accordion. */
  curl_example?: string;
  /** Optional Python example for the examples accordion. */
  python_example?: string;
}

// ---------------------------------------------------------------------------
// Endpoint execution result types
// ---------------------------------------------------------------------------

/** Discriminated type of an executed endpoint response. */
export type ExecutionResultType =
  | "image" // image/png, image/jpeg, image/webp, etc.
  | "tile" // image/* from a tile endpoint (hint: can be placed on map)
  | "geojson" // application/geo+json OR application/json with GeoJSON body
  | "json" // generic application/json
  | "text" // text/plain, text/html
  | "binary" // application/octet-stream
  | "unknown"; // unrecognised content type

/** Raw HTTP response before interpretation. */
export interface RawExecutionResponse {
  body: ArrayBuffer;
  contentType: string;
  status: number;
  sizeBytes: number;
}

/** Typed and interpreted result of executing an API endpoint. */
export interface ExecutionResult {
  type: ExecutionResultType;
  contentType: string;
  status: number;
  sizeBytes: number;
  isTruncated: boolean;
  /** Object URL created with URL.createObjectURL — MUST be revoked in ngOnDestroy. */
  blobUrl?: string;
  /** Parsed JSON/GeoJSON body (only present if !isTruncated). */
  parsedJson?: unknown;
  /** Plain text body (only present if !isTruncated). */
  textContent?: string;
  /** Suggested download filename (from Content-Disposition or inferred). */
  filename?: string;
}

/** Error from executing an API endpoint. */
export interface ExecutionError {
  httpStatus?: number;
  message: string;
  /** Parsed error body if available. */
  detail?: unknown;
  isNetworkError: boolean;
}

// ---------------------------------------------------------------------------
// Unified data result — origin-agnostic visualization
// ---------------------------------------------------------------------------

/**
 * Maps a LangGraph/CopilotKit tool name to an Angular component class
 * that will be rendered whenever that tool call appears in the chat.
 */
export interface ToolRendererConfig {
  toolName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: new (...args: any[]) => { toolCall: unknown };
  agentId?: string;
}
